/**
 * Shopify storefront cart helpers for the booking-agent widget.
 *
 * These only work when the widget runs same-origin inside the Shopify
 * storefront (embed mode) — the AJAX Cart API lives at /cart/add.js on the
 * store domain. After adding, we set the `referral` cart attribute to the
 * stylist's unique code: the order webhook reads note_attributes.referral
 * to credit the sale to the stylist (shopify/webhook_processors_v2.py),
 * matching the attributed cart permalinks the store already uses.
 */

export interface ShopifyCartAddResult {
  success: boolean;
  error?: string;
}

// Locale-prefixed storefronts (e.g. /en-au/) expose the Ajax Cart API under
// Shopify's locale-aware root — build endpoints from window.Shopify.routes.root.
export const getShopifyCartRoot = (): string => {
  if (typeof window !== 'undefined') {
    const root = (window as unknown as { Shopify?: { routes?: { root?: string } } })
      .Shopify?.routes?.root;
    if (root) {
      return root;
    }
  }
  return '/';
};

export const addVariantToShopifyCart = async (
  variantId: string | number,
  referralCode?: string,
): Promise<ShopifyCartAddResult> => {
  const numericId = Number(variantId);
  if (!numericId) {
    return { success: false, error: 'This product cannot be added online.' };
  }
  const cartRoot = getShopifyCartRoot();
  try {
    const res = await fetch(`${cartRoot}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: numericId, quantity: 1 }] }),
    });
    if (!res.ok) {
      let message = 'Could not add to cart.';
      try {
        const data = await res.json();
        message = data?.description || data?.message || message;
      } catch {
        // keep default message
      }
      return { success: false, error: message };
    }
    if (referralCode) {
      // Stylist commission attribution — best-effort; the add succeeded.
      try {
        await fetch(`${cartRoot}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { referral: referralCode } }),
        });
      } catch {
        // attribution failure must not undo a successful add
      }
    }
    return { success: true };
  } catch {
    return { success: false, error: 'Could not reach the store cart.' };
  }
};

/** Current cart item count via GET {root}cart.js — null when unreachable. */
export const getShopifyCartCount = async (): Promise<number | null> => {
  try {
    const res = await fetch(`${getShopifyCartRoot()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.item_count === 'number' ? data.item_count : null;
  } catch {
    return null;
  }
};

/** Open the theme's cart drawer when the storefront exposes it. */
export const openShopifyCartDrawer = (): boolean => {
  if (typeof window === 'undefined') return false;
  const w = window as unknown as {
    HUSLCart?: { open?: () => void };
    openCartDrawer?: () => void;
  };
  const open = w.HUSLCart?.open || w.openCartDrawer;
  if (typeof open !== 'function') return false;
  try {
    open();
    return true;
  } catch {
    return false;
  }
};

/** Best-effort refresh of the theme's cart UI (badge/sticky/drawer) after an add. */
export const refreshShopifyCartUi = (): void => {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { HUSLCart?: { refresh?: () => void } };
  try {
    w.HUSLCart?.refresh?.();
  } catch {
    // theme not present — the in-chat bar still reflects the count
  }
};
